import React, { useState, useEffect, useCallback, useRef } from 'react';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { BASE_URL } from '../constants/config';
import { fetchWithAuth } from '../constants/api';
import { showToast, showAlert } from '../components/Toast';
import AppShell from '../components/AppShell';
import Modal from '../components/Modal';
import { formatDate, formatINR, STATUS_STYLES, TXN_STYLES, getCategoryIcon } from '../utils/expenseFormat';

// ── Helpers ──────────────────────────────────────────────────────────────────

const groupExpensesByDate = (data) => {
  const groups = {};
  data.forEach(item => {
    const dateStr = formatDate(item.Date);
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(item);
  });

  const todayStr = formatDate(new Date().toISOString());

  return Object.keys(groups).sort((a, b) => {
    const dateA = new Date(groups[a][0].Date || '').getTime();
    const dateB = new Date(groups[b][0].Date || '').getTime();
    return dateB - dateA;
  }).map(dateStr => {
    const sectionData = groups[dateStr];
    const total = sectionData.reduce((sum, item) => sum - (item.Amount || 0), 0);
    return {
      title: dateStr === todayStr ? 'Today' : dateStr,
      total,
      data: sectionData,
    };
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const ExpenseListScreen = ({ navigate, onLogout, params, onEmpChange, onScrollCapture }) => {
  const [expenses, setExpenses] = useState([]);
  const [balanceInfo, setBalanceInfo] = useState({ TotalCredits: 0, TotalExpenses: 0, Balance: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const contentRef = useRef(null);

  const [empCode] = useState(() => localStorage.getItem(STORAGE_KEYS.EMP_CODE) || '');
  const [empId] = useState(() => {
    const id = localStorage.getItem(STORAGE_KEYS.EMP_ID);
    return id ? parseInt(id, 10) : null;
  });
  const [empName] = useState(() => localStorage.getItem(STORAGE_KEYS.USER_NAME) || 'Self');

  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [selectedEmpName, setSelectedEmpName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeModalVisible, setEmployeeModalVisible] = useState(false);

  const getToken = () => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  const fetchBalance = useCallback(async (id) => {
    if (!id) return;
    try {
      const response = await fetchWithAuth(`${BASE_URL}api/Expense/EMPACCredit/Balance/${id}`);
      if (response.ok) setBalanceInfo(await response.json());
    } catch (_) {}
  }, []);

  const fetchEmployeesList = useCallback(async () => {
    setEmployeesLoading(true);
    try {
      const response = await fetchWithAuth(`${BASE_URL}api/Employee/List`);
      if (response.ok) setEmployees(await response.json());
    } catch (_) {} finally {
      setEmployeesLoading(false);
    }
  }, []);

  const fetchExpenses = useCallback(async (isRefresh = false, targetEmpId) => {
    const idToFetch = targetEmpId ?? selectedEmpId;
    if (!idToFetch) { setLoading(false); return; }
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const response = await fetchWithAuth(`${BASE_URL}api/Expense/List?InsertedBy=${idToFetch}`);
      if (response.ok) setExpenses(await response.json());
      else showToast(`Failed to load expenses (${response.status})`, 'error');
      await fetchBalance(idToFetch);
    } catch (_) {
      showToast('Could not load expenses. Please check your connection.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedEmpId, fetchBalance]);

  useEffect(() => {
    const init = async () => {
      // If returning from a sub-screen with a preserved employee, restore it
      if (params?.preservedEmpId) {
        const id = params.preservedEmpId;
        const name = params.preservedEmpName || '';
        setSelectedEmpId(id);
        setSelectedEmpName(name);
        if (empCode?.toUpperCase() === 'A') fetchEmployeesList();
        fetchExpenses(false, id);
      } else {
        const id = empId;
        setSelectedEmpId(id);
        setSelectedEmpName(empName);
        if (empCode?.toUpperCase() === 'A') fetchEmployeesList();
        if (id) fetchExpenses(false, id);
        else setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line

  // Restore scroll position once the list has finished loading (e.g. returning from Details)
  useEffect(() => {
    if (!loading && params?.preservedScrollTop && contentRef.current) {
      contentRef.current.scrollTop = params.preservedScrollTop;
    }
  }, [loading]); // eslint-disable-line

  // Continuously report scroll position up to App so it can be restored on back-navigation
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !onScrollCapture) return;
    const handleScroll = () => onScrollCapture(el.scrollTop);
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [loading, onScrollCapture]);

  const handleLogout = () => {
    showAlert('Logout', 'Are you sure you want to logout?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Logout', style: 'destructive', onPress: () => {
          localStorage.clear();
          onLogout?.();
        }
      },
    ]);
  };

  const handleDelete = (id) => {
    showAlert('Delete Expense', 'Are you sure you want to delete this expense?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete', style: 'destructive', onPress: async () => {
          try {
            setLoading(true);
            const response = await fetchWithAuth(`${BASE_URL}api/Expense/Delete/${id}`, {
              method: 'DELETE',
            });
            if (response.ok) {
              showToast('Expense deleted successfully.', 'success');
              fetchExpenses();
            } else {
              showToast(`Failed to delete expense (${response.status})`, 'error');
              setLoading(false);
            }
          } catch (_) {
            showToast('Could not delete expense.', 'error');
            setLoading(false);
          }
        }
      },
    ]);
  };

  const sections = groupExpensesByDate(expenses);

  if (loading) {
    return (
      <AppShell title="Expenses" rightAction={{ icon: '⏻', onClick: handleLogout, label: 'Logout' }}>
        <div className="loading-screen">
          <div className="spinner spinner--gold" />
          <p>Loading expenses...</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Expenses"
      rightAction={{ icon: '⏻', onClick: handleLogout, label: 'Logout' }}
      rightAction2={{ icon: '↻', onClick: () => fetchExpenses(true), label: 'Refresh' }}
      contentRef={contentRef}
      bottomNav={empCode?.toUpperCase() === 'A' && (
        <div className="bottom-nav">
          <button className="bottom-nav__item" onClick={() => navigate('ExpenseApproval')}>
            <span className="bottom-nav__icon">✓</span>
            <span className="bottom-nav__label">Approvals</span>
          </button>
          <button className="bottom-nav__item" onClick={() => {
            onEmpChange?.(selectedEmpId, selectedEmpName);
            navigate('EmpCreditForm', { empId: selectedEmpId, empName: selectedEmpName });
          }}>
            <span className="bottom-nav__icon">💰</span>
            <span className="bottom-nav__label">Add Credit</span>
          </button>
          <button className="bottom-nav__item bottom-nav__item--primary" onClick={() => {
            onEmpChange?.(selectedEmpId, selectedEmpName);
            navigate('ExpenseForm');
          }}>
            <span className="bottom-nav__icon">＋</span>
            <span className="bottom-nav__label">Add Expense</span>
          </button>
        </div>
      )}
    >
      {/* Employee Selector (admin only) */}
      {empCode?.toUpperCase() === 'A' && (
        <button
          className="emp-selector"
          onClick={() => setEmployeeModalVisible(true)}
        >
          <div className="emp-selector__info">
            <div className="emp-selector__label">Viewing Data For:</div>
            <div className="emp-selector__name">{selectedEmpName}</div>
          </div>
          <span className="emp-selector__arrow">›</span>
        </button>
      )}

      {/* Balance Card */}
      <div className="balance-card">
        <div className="balance-row">
          <button
            className="balance-item"
            onClick={() => { if (selectedEmpId) {
            onEmpChange?.(selectedEmpId, selectedEmpName);
            navigate('EmpCreditList', { empId: selectedEmpId });
          }}}
          >
            <div className="balance-label">Credits</div>
            <div className="balance-value" style={{ color: '#1a7a3c' }}>
              {formatINR(balanceInfo.TotalCredits).replace('₹ ', '')}
            </div>
          </button>
          <span className="balance-op">−</span>
          <div className="balance-item">
            <div className="balance-label">Expenses</div>
            <div className="balance-value" style={{ color: '#c62828' }}>
              {formatINR(balanceInfo.TotalExpenses).replace('₹ ', '')}
            </div>
          </div>
          <span className="balance-op">=</span>
          <div className="balance-item">
            <div className="balance-label">Balance</div>
            <div className="balance-value" style={{ color: '#C8973A', fontWeight: 700 }}>
              {formatINR(balanceInfo.Balance).replace('₹ ', '')}
            </div>
          </div>
        </div>
      </div>

      {/* Expense List */}
      {refreshing && (
        <div style={{ textAlign: 'center', padding: '12px', color: '#999', fontSize: 13 }}>
          Refreshing...
        </div>
      )}

      {sections.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: 60, color: '#aaa', fontSize: 15 }}>
          No expenses found.
        </div>
      ) : (
        <div className="expense-list" style={{ paddingBottom: empCode?.toUpperCase() === 'A' ? 20 : 100 }}>
          {sections.map(section => (
            <div key={section.title}>
              {/* Section Header */}
              <div className="section-header">
                <span className="section-title">{section.title}</span>
                <span className="section-total" style={{ color: '#999' }}>
                  {section.total < 0 ? '−' : ''}{formatINR(Math.abs(section.total)).replace('₹ ', '')}
                </span>
              </div>
              {/* Items */}
              {section.data.map(item => {
                const catStyle = getCategoryIcon(item.ExpCatName);
                return (
                  <div
                    key={item.ID}
                    className="expense-item"
                    onClick={() => {
                      onScrollCapture?.(contentRef.current?.scrollTop || 0);
                      navigate('ExpenseDetail', { expense: item });
                    }}
                  >
                    <div className="expense-icon" style={{ background: catStyle.color }}>
                      {catStyle.icon}
                    </div>
                    <div className="expense-content">
                      <div className="expense-party">
                        {item.ExpPartyName || 'No Party'}
                        {STATUS_STYLES[item.Status] && (
                          <span
                            className="expense-status-badge"
                            style={{ color: STATUS_STYLES[item.Status].color, background: STATUS_STYLES[item.Status].bg }}
                            title={STATUS_STYLES[item.Status].label}
                            aria-label={STATUS_STYLES[item.Status].label}
                          >
                            {STATUS_STYLES[item.Status].icon}
                          </span>
                        )}
                      </div>
                      <div className="expense-cat">
                        {item.ExpCatName || 'Other'}
                        {item.ExpSubCatName ? ` › ${item.ExpSubCatName}` : ''}
                      </div>
                      <div className="expense-meta-row">
                        {item.InsertedOn && (
                          <span className="expense-inserted-on">📅 {formatDate(item.InsertedOn)}</span>
                        )}
                        {TXN_STYLES[item.TransactionType] && (
                          <span className="expense-txn-tag" style={{ color: TXN_STYLES[item.TransactionType].color }}>
                            {TXN_STYLES[item.TransactionType].icon} {TXN_STYLES[item.TransactionType].label}
                          </span>
                        )}
                      </div>
                      {item.Status === 'Rejected' && item.RejectionReason && (
                        <div className="expense-rejection-reason">⚠️ {item.RejectionReason}</div>
                      )}
                    </div>
                    <div className="expense-amount" style={{ color: '#c62828' }}>
                      −{formatINR(item.Amount || 0).replace('₹ ', '')}
                    </div>
                    <button
                      className="expense-delete-btn"
                      onClick={e => { e.stopPropagation(); handleDelete(item.ID); }}
                      aria-label="Delete"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* FAB (employees only — admin uses the bottom nav instead) */}
      {empCode?.toUpperCase() !== 'A' && (
        <div className="fab-container">
          <button className="fab fab--primary" onClick={() => {
            onEmpChange?.(selectedEmpId, selectedEmpName);
            navigate('ExpenseForm');
          }}>
            <span>＋</span> Add Expense
          </button>
        </div>
      )}

      {/* Employee Modal */}
      <Modal
        visible={employeeModalVisible}
        onDismiss={() => setEmployeeModalVisible(false)}
        title="Select Employee"
      >
        {employeesLoading ? (
          <div className="loading-screen" style={{ minHeight: 120 }}>
            <div className="spinner spinner--gold" />
          </div>
        ) : (
          employees.map(emp => (
            <div
              key={emp.ID}
              className="list-item"
              onClick={() => {
                setSelectedEmpId(emp.ID);
                setSelectedEmpName(emp.EmpName);
                setEmployeeModalVisible(false);
                fetchExpenses(false, emp.ID);
                onEmpChange?.(emp.ID, emp.EmpName);
              }}
            >
              <div className="list-item__text">
                <div className="list-item__title">{emp.EmpName}</div>
              </div>
              {selectedEmpId === emp.ID && <span className="list-item__check">✓</span>}
            </div>
          ))
        )}
      </Modal>

      <style>{`
        .emp-selector {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f0f4f8;
          margin: 12px;
          padding: 10px 14px 10px 16px;
          border-radius: 12px;
          border: 1px solid #d1d9e6;
          cursor: pointer;
          width: calc(100% - 24px);
          text-align: left;
        }
        .emp-selector__label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
        .emp-selector__name { font-size: 15px; font-weight: 700; color: #333; }
        .emp-selector__arrow { font-size: 22px; color: #999; }
        .balance-card {
          background: #f8f9fa;
          padding: 12px 16px;
          border-bottom: 1px solid #eee;
        }
        .balance-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .balance-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
        }
        .balance-label {
          font-size: 11px;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .balance-value {
          font-size: 15px;
          font-weight: 600;
        }
        .balance-op {
          font-size: 16px;
          color: #999;
          padding-top: 16px;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          padding: 8px 16px;
          background: #fff;
          border-bottom: 0.5px solid #eee;
          margin-top: 2px;
        }
        .section-title { font-size: 12px; color: #999; font-weight: 400; }
        .section-total { font-size: 12px; color: #999; font-weight: 400; }
        .expense-item {
          display: flex;
          align-items: center;
          padding: 9px 16px;
          background: #fff;
          border-bottom: 0.5px solid #f0f0f0;
          cursor: pointer;
          transition: background 0.15s;
        }
        .expense-item:hover { background: #fafafa; }
        .expense-icon {
          width: 34px;
          height: 34px;
          border-radius: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
          margin-right: 10px;
        }
        .expense-content { flex: 1; min-width: 0; }
        .expense-party {
          font-size: 14px;
          font-weight: 500;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .expense-cat {
          font-size: 12px;
          color: #999;
          margin-top: 1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .expense-meta-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 1px;
        }
        .expense-inserted-on {
          font-size: 10px;
          color: #b0b0b0;
        }
        .expense-txn-tag {
          font-size: 10px;
          font-weight: 600;
        }
        .expense-status-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          margin-left: 6px;
          border-radius: 50%;
          font-size: 10px;
          line-height: 1;
          vertical-align: middle;
        }
        .expense-rejection-reason {
          font-size: 10px;
          color: #c62828;
          margin-top: 1px;
        }
        .expense-amount {
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
          margin-right: 2px;
        }
        .expense-delete-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          padding: 6px;
          margin-left: 2px;
          border-radius: 8px;
          color: #b0b0b0;
          cursor: pointer;
          transition: color 0.15s, background 0.15s;
          flex-shrink: 0;
        }
        .expense-delete-btn:hover { color: #c62828; background: #fdeaea; }
      `}</style>
    </AppShell>
  );
};

export default ExpenseListScreen;
